import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { DateRangeField, isoDay, monthsAgoIso, useDateRange, useEscapeKey } from '@odo/shared-ui';
import { exportBillingInvoicesCsv } from '../../../services/billing';
import { parseBlobErrorResponse, toApiError } from '../../../services/apiErrors';
import { downloadBlob } from '../../../utils/downloadBlob';

const DEFAULT_RANGE_MONTHS = 3;

export interface ExportInvoicesModalProps {
    onClose: () => void;
}

// No practice/patient picker here, unlike the admin export modal - a doctor
// only ever has their own practice, and the endpoint enforces that server-side.
export function ExportInvoicesModal({ onClose }: ExportInvoicesModalProps) {
    const range = useDateRange({ defaultMonths: DEFAULT_RANGE_MONTHS });
    const { from, to, isInvalid } = range;
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEscapeKey(onClose, !isExporting);

    const buildFilename = (): string =>
        `invoices_${from || monthsAgoIso(DEFAULT_RANGE_MONTHS)}_${to || isoDay(new Date())}.csv`;

    const handleExport = async () => {
        if (isInvalid || isExporting) return;
        setIsExporting(true);
        setError(null);
        try {
            const { blob, truncated } = await exportBillingInvoicesCsv({
                from: from || undefined,
                to: to || undefined
            });
            downloadBlob(blob, buildFilename());
            // The file still downloads - the range just needs narrowing for
            // the rest, so the dialog stays open to say so.
            if (truncated) {
                setError('Too many invoices for one file. Narrow the range and export again.');
                return;
            }
            onClose();
        } catch (err) {
            setError(toApiError(await parseBlobErrorResponse(err)).message);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="export-invoices-title"
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-6"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget && !isExporting) onClose();
            }}
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[480px] flex flex-col">
                <div className="px-6 py-4 border-b border-gray-200 shrink-0 flex items-start justify-between gap-4">
                    <div>
                        <h2 id="export-invoices-title" className="text-lg font-bold text-gray-800">
                            Export invoices
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isExporting}
                        aria-label="Close"
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors shrink-0 disabled:opacity-50"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="p-6 flex flex-col gap-5">
                    <DateRangeField
                        range={range}
                        label="Created between"
                        hint={`Defaults to the last ${DEFAULT_RANGE_MONTHS} months.`}
                        idPrefix="doctor-export"
                    />
                    {error && <p className="text-sm text-red-500">{error}</p>}
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex items-center gap-3 shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isExporting}
                        className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => void handleExport()}
                        disabled={isExporting || isInvalid}
                        className="ml-auto bg-black text-white rounded-xl px-6 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors shadow disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isExporting ? 'Exporting…' : 'Export'}
                    </button>
                </div>
            </div>
        </div>
    );
}
